import { Spinner } from "@visiblethread/react-components";
import { USER_ROLES } from "constants/app";
import { useWorkspaceSync } from "hooks/generic/useWorkspaceSync";
import { any } from "prop-types";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getSessionInfo } from "slices/app/actions/app";
import {
  selectApplication,
  selectHasNoSession,
  selectIsCloudMode,
  selectIsInitialized,
  selectIsLoggedIn,
  selectIsSso,
  selectIsSwitchingWorkspace,
  selectUserRoles,
  selectUserWorkspaceId,
} from "slices/app/selectors";
import { fetchCurrencies } from "slices/currency";
import { loadFeatureFlags } from "slices/featureFlags";
import { hasRole } from "utils";

import useEntityWorkspaceResolution from "./useEntityWorkspaceResolution";

const SessionResolver = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useWorkspaceSync();
  useEntityWorkspaceResolution();

  const application = useSelector(selectApplication);
  const isInitialized = useSelector(selectIsInitialized);
  const hasNoSession = useSelector(selectHasNoSession);

  const isCloudMode = useSelector(selectIsCloudMode);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isSsoAuthType = useSelector(selectIsSso);
  const isSwitchingWorkspace = useSelector(selectIsSwitchingWorkspace);
  const roles = useSelector(selectUserRoles);
  const workspaceId = useSelector(selectUserWorkspaceId);

  const isFeatureFlagOnly =
    hasRole(roles, "feature_flag") && !hasRole(roles, "user");
  const isOnPremSsoAdminLogin =
    isCloudMode === false && window.location.hash.includes("/systemAdminLogin");
  const isWaitingForSamlRedirect =
    isInitialized &&
    isSsoAuthType &&
    isLoggedIn === false &&
    !isOnPremSsoAdminLogin;

  useEffect(() => {
    if (hasNoSession) {
      dispatch(getSessionInfo(navigate));
    }
  }, [location.hash, navigate, hasNoSession]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(loadFeatureFlags());
    }
  }, [isLoggedIn, dispatch]);

  // /api/currencies sits under the hasRole("USER") catch-all, and errorHandler turns the 403 a
  // feature-flag-only or customer-success-only session would get into a logout.
  useEffect(() => {
    if (hasRole(roles, USER_ROLES.user)) {
      dispatch(fetchCurrencies());
    }
  }, [roles, workspaceId, dispatch]);


  if (
    !hasNoSession &&
    !window.location.hash.includes("/setup") &&
    application?.licensed === false &&
    isCloudMode === false &&
    !isFeatureFlagOnly
  ) {
    navigate("/setup");
  }

  if (!isInitialized || isSwitchingWorkspace) {
    return <Spinner fullScreen size="62px" />;
  }
  if (isWaitingForSamlRedirect) {
    return null;
  }
  // never show login page for authenticated user ever
  if (isLoggedIn && location.pathname === "/login") {
    return <Navigate to="/" replace />;
  }
  return children;
};

SessionResolver.propTypes = {
  children: any,
};

export default SessionResolver;
